import type { PasswordImportPreview } from "@/lib/auth/better-auth/password-import-types";
import type { RetirementProgress } from "@/lib/auth/better-auth/readiness";
import type { MigrationAction, PasswordSetup } from "@/lib/auth/transition";
import type { AuthRuntime } from "@/lib/auth/runtime";
import type {
  MigrationInput,
  MigrationPreview,
} from "@/lib/auth/better-auth/migration";
import type {
  EmailReview,
  EmailUpdateInput,
  EmailUpdateResult,
} from "@/lib/auth/better-auth/emails";
import apiClient from "@/services/api-client";
import { betterAuthClient } from "@/lib/auth/better-auth/client";
import type {
  ProviderInput,
  ProviderSummary,
} from "@/lib/auth/better-auth/providers";

export interface SetupInput {
  token: string;
  name: string;
  email: string;
  password: string;
}
export interface ManagedConnections {
  connections: ProviderSummary[];
  callbackBaseUrl: string;
}
export type TransitionStatus = AuthRuntime & {
  emailConfigured: boolean;
  progress?: RetirementProgress;
};

// React Query expects rejected promises for failures; the auth SDK returns an
// explicit error result. Keep the service's existing { data } response shape.
async function authResult<T>(
  result: Promise<{ data: T | null; error: { message?: string } | null }>
) {
  const { data, error } = await result;
  if (error || data === null)
    throw new Error(error?.message || "Authentication failed");
  return { data };
}

export const AuthConnectionsService = {
  setupStatus: () => apiClient.get<{ required: boolean }>("/api/auth/setup"),
  setup: (input: SetupInput) =>
    apiClient.post<{ success: boolean }>("/api/auth/setup", input),
  passwordLogin: (email: string, password: string) =>
    apiClient.post("/api/auth/custom/login", { email, password }),
  transitionStatus: () =>
    apiClient.get<TransitionStatus>("/api/auth/transition"),
  transition: (
    action: MigrationAction,
    revision: number,
    confirmationFingerprint?: string,
    passwordSetup?: PasswordSetup
  ) =>
    apiClient.post("/api/auth/transition", {
      action,
      revision,
      confirmationFingerprint,
      passwordSetup,
    }),
  resetPassword: (token: string, newPassword: string) =>
    authResult(betterAuthClient.resetPassword({ token, newPassword })),
  migration: (params?: { source?: string; target?: string }) =>
    apiClient.get<MigrationPreview>("/api/auth/migration", { params }),
  migrate: (input: MigrationInput) =>
    apiClient.post<{
      imported?: number;
      repaired?: number;
      sent?: string[];
      failed?: string[];
      skipped?: string[];
    }>("/api/auth/migration", input),
  migrationEmails: () =>
    apiClient.get<EmailReview>("/api/auth/migration/emails"),
  updateMigrationEmails: (input: EmailUpdateInput) =>
    apiClient.post<EmailUpdateResult>("/api/auth/migration/emails", input),
  previewPasswords: (file: File) => {
    const body = new FormData();
    body.set("action", "preview-passwords");
    body.set("file", file);
    return apiClient.post<PasswordImportPreview>("/api/auth/migration", body);
  },
  importPasswords: (file: File, fingerprint: string, userIds: string[]) => {
    const body = new FormData();
    body.set("action", "import-passwords");
    body.set("file", file);
    body.set("fingerprint", fingerprint);
    userIds.forEach((id) => body.append("userIds", id));
    return apiClient.post<{ imported: number }>("/api/auth/migration", body);
  },
  list: () => apiClient.get<ManagedConnections>("/api/auth/connections"),
  save: (input: ProviderInput) =>
    apiClient.put<ManagedConnections>("/api/auth/connections", input),
  remove: (id: string) =>
    apiClient.delete<ManagedConnections>("/api/auth/connections", {
      data: { id },
    }),
  environment: () =>
    apiClient.get<{
      AUTH_BACKEND?: string;
      BETTER_AUTH_ENABLED?: string;
      AUTH0_ENABLED?: string;
      AUTH_EMAIL_ENABLED?: string;
      AUTH_MIGRATION_PHASE?: string;
    }>("/api/environment"),
  accounts: () => authResult(betterAuthClient.listAccounts()),
  link: (
    provider: string,
    callbackURL: string,
    strategy?: string
  ): Promise<{ data: { url: string } }> =>
    strategy === "samlp"
      ? apiClient.post<{ url: string }>("/api/auth/better-auth/link-saml", {
          providerId: provider,
          callbackURL,
        })
      : authResult(
          betterAuthClient.linkSocial({
            provider,
            callbackURL,
            errorCallbackURL: callbackURL,
            disableRedirect: true,
          })
        ),
};
